/**
 * Crew Timesheet Page
 *
 * Shows detailed timesheet for a specific crew member.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCrewTimesheet, downloadReportCSV, TimesheetEntryData, CrewTimesheetResponse } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange, ScheduledHoursBadge } from '../../../components/reports';
import { formatInTimeZone } from '../../../utils/dateFormatting';

export default function CrewTimesheetPage() {
  const { id: crewId } = useParams<{ id: string }>();
  const [dateRange, setDateRange] = useReportDateRange();
  const [data, setData] = useState<CrewTimesheetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!crewId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getCrewTimesheet(crewId, {
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setData(response);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [crewId, dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    if (!crewId) return;

    setIsExporting(true);
    try {
      await downloadReportCSV(
        `/reports/crew/${crewId}/timesheet`,
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        `crew-timesheet-${crewId}.csv`
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (cents: number | null) => {
    if (cents === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return formatInTimeZone(dateString, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatInTimeZone(dateString, {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatRate = (rate: number | null, rateType: string | null) => {
    if (rate === null) return '-';
    const formatted = formatCurrency(rate);
    return rateType === 'FLAT' ? `${formatted} (flat)` : `${formatted}/hr`;
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      CONFIRMED: 'bg-green-100 text-green-800',
      ASSIGNED: 'bg-yellow-100 text-yellow-800',
      DECLINED: 'bg-red-100 text-red-800',
      REMOVED: 'bg-gray-100 text-gray-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate totals
  const totals = data?.report.reduce(
    (acc, row) => ({
      totalHours: acc.totalHours + (row.hoursWorked || 0),
      totalPay: acc.totalPay + (row.totalPay || 0),
    }),
    { totalHours: 0, totalPay: 0 }
  ) || { totalHours: 0, totalPay: 0 };

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports/payroll" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Payroll Report
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {data?.crew ? `${data.crew.firstName} ${data.crew.lastName}` : 'Crew Member'} - Timesheet
        </h1>
        {data?.crew && (
          <p className="text-gray-600 mt-1">{data.crew.email}</p>
        )}
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : !data || data.report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No timesheet entries for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Check In</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Check Out</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pay</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.report.map((row: TimesheetEntryData, index: number) => (
                  <tr key={`${row.eventId}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(row.eventDate)}</td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/events/${row.eventId}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {row.eventName}
                      </Link>
                      <p className="text-sm text-gray-500">{row.venue}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.roleName}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">
                      {formatTime(row.checkInTime)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">
                      {formatTime(row.checkOutTime)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {row.hoursWorked !== null ? row.hoursWorked.toFixed(1) : '-'}
                      {row.hoursDefaulted && <ScheduledHoursBadge />}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {formatRate(row.effectiveRate, row.effectiveRateType)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalPay)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadge(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={5} className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {totals.totalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm text-gray-900">-</td>
                  <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                    {formatCurrency(totals.totalPay)}
                  </td>
                  <td className="px-6 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
